import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@mantine/core';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FileInputControl, Form } from '@/core/components';

import { useUploadNotification } from '@feature/utils/upload/fileUpload';

import { useTravelReferenceDocumentUpload } from '../model/TravelReferenceDocumentQuery';
import {
  TravelReferenceDocumentUploadSchema,
  getTravelReferenceDocumentUploadSchema,
} from '../model/TravelReferenceDocumentSchema';
import { TravelReferenceDocumentSlotRef, travelReferenceDocumentAccept } from '../model/TravelReferenceDocumentTypes';
import { getTravelReferenceDocumentSlotLabel } from '../travelReferenceDocumentLabels';

interface TravelReferenceDocumentUploadModalProps {
  slot: TravelReferenceDocumentSlotRef;
  onClose: () => void;
}

export function TravelReferenceDocumentUploadModal({ slot, onClose }: TravelReferenceDocumentUploadModalProps) {
  const { t } = useTranslation();
  const notification = useUploadNotification();
  const { mutateAsync, isPending } = useTravelReferenceDocumentUpload();
  const schema = useMemo(() => getTravelReferenceDocumentUploadSchema(t), [t]);

  const form = useForm<TravelReferenceDocumentUploadSchema>({
    resolver: zodResolver(schema),
  });

  // Promise handlers still run after an unmount, unlike mutate-level callbacks.
  const handleSubmit = (values: TravelReferenceDocumentUploadSchema) =>
    mutateAsync({ kind: slot.kind, role: slot.role, file: values.file }).then(() => {
      notification.success();
      onClose();
    }, notification.error);

  const handleClose = () => {
    if (!isPending) onClose();
  };

  return (
    <Modal
      opened
      centered
      onClose={handleClose}
      closeButtonProps={{ disabled: isPending }}
      title={getTravelReferenceDocumentSlotLabel(t, slot)}
      size="md"
    >
      <Form
        onSubmit={form.handleSubmit(handleSubmit)}
        onCancel={handleClose}
        isSubmitting={isPending}
      >
        <FileInputControl
          name="file"
          label={t('travel.referenceDocuments.file')}
          accept={travelReferenceDocumentAccept}
          isSubmitting={isPending}
          withAsterisk
          control={form.control}
        />
      </Form>
    </Modal>
  );
}
